/**
 * Content loader - fetches the modular v2 content (taxonomy / weights /
 * descriptions / interventions) in parallel and assembles a single
 * `UnifiedContent` object.
 *
 * Roadmaps are NOT files. They are computed per user from their assessment
 * scores by `roadmap.ts#buildPersonalRoadmap`. There is intentionally no
 * `programs` field and no legacy fallback - the 4 modular files are the
 * single source of truth.
 */

export interface PoleConfig {
  label: string;
  icon?: string;
  description?: string;
}

/**
 * One-liner insight под label - формат «фраза, що користувач чує в голові»
 * або «коротка self-recognition framing». Опціональне, override з descriptions.{lang}.json.
 */
export type Subtitle = string;

export interface PendulumAxis {
  id: string;
  label: string;
  subtitle?: Subtitle;
  question?: string;
  description?: string;
  score_note?: string;
  mechanism?: string;
  real_life_examples?: string[];
  aha_insight?: string;
  flexible_middle?: string;
  how_it_helps?: string[];
  regulation_strategies?: string[];
  research?: { key_study?: string; reference?: string; source_url?: string };
  left_pole: string;
  right_pole: string;
  poles: Record<string, PoleConfig>;
  // Inverted view of weights.json → consumed by scoringEngine.
  weights: Record<string, Record<string, number>>;
  // Engine-tag counter-evidence weights (damp the amplitude), also from weights.json.
  centering?: Record<string, number>;
}

export interface DimensionContent {
  id: string;
  label: string;
  subtitle?: Subtitle;
  color?: string;
  icon?: string;
  question?: string;
  description?: string;
  what_we_measure?: string[];
  research_foundations?: any[];
  aha_insights?: any[];
  common_problems?: any[];
  theory?: string[];
  how_it_helps?: string[];
  level_descriptions?: Record<string, any>;
}

export interface TagContent {
  id: string;
  label: string;
  subtitle?: Subtitle;
  type: 'shadow' | 'engine';
  description?: string;
  mechanism?: string;
  aha_insight?: string;
  research?: { key_study?: string; reference?: string; source_url?: string };
  recommendations?: string[];
  real_life_examples?: string[];
  how_it_helps?: string[];
  score_note?: string;
  primary_dimension?: string;
  dimension_weights: Record<string, number>; // pattern_to_dimension[id]
  pendulum_weights: Record<string, Record<string, number>>; // pattern_to_pendulum[id]
}

export interface InterventionDosage {
  duration_minutes?: number | null;
  frequency?: string;
  horizon_weeks?: number;
  cognitive_load?: 'low' | 'medium' | 'high' | string;
}

export interface Intervention {
  id: string;
  name: string;
  primary_dimension: string;
  modality?: string;
  dosage: InterventionDosage;
  instructions: { what?: string; why?: string; how?: string; time_label?: string };
  contributes_to_tags: Record<string, number>;
  researches: Array<{ finding: string; reference?: string; evidence_grade?: string }>;
}

export interface MonthSpec {
  id: string;
  label: string;
  tagline: string;
  narrative: string;
}

/**
 * UI text catalog. Every dashboard label, button caption, section title, and
 * empty-state copy lives here so content stays separable from code (and is
 * trivially translatable).
 *
 * Templates use `{token}` placeholders that components resolve at render time.
 */
export interface UICopy {
  greetings: { night: string; morning: string; afternoon: string; evening: string; fallback_name: string };
  nav: {
    dashboard: string;
    sign_out: string;
    recalculate: string;
    recalculate_tooltip: string;
    recalculate_confirm: string;
  };
  sections: {
    dimensions: { title: string; subtitle: string; meta_label: string };
    patterns: { title: string; subtitle: string; meta_label: string; empty: string; xp_tooltip: string };
    pendulums: { title: string; subtitle: string; meta_label: string; xp_tooltip: string };
    roadmap: {
      title: string;
      subtitle: string;
      stat_done_label: string;
      stat_complete_label: string;
      month_eyebrow: string;
      panel_eyebrow: string;
      locked_label: string;
      locked_hint: string;
      task_research_prefix: string;
      task_contributes_label: string;
      task_how_label: string;
      task_why_label: string;
      task_more_label: string;
      task_less_label: string;
      metrics_title: string;
      retake_title: string;
      retake_text: string;
      retake_button: string;
    };
  };
    today_focus: {
      eyebrow: string;
      cta_pending: string;
      cta_done: string;
      research_prefix: string;
      contributes_label: string;
      how_label: string;
      why_label: string;
      more_label: string;
      less_label: string;
      duration_prefix: string;
    };
  states: {
    loading: string;
    no_results_icon: string;
    no_results_title: string;
    no_results_body: string;
    no_results_cta: string;
    paywall_icon: string;
    paywall_title: string;
    paywall_body: string;
    paywall_cta: string;
  };
  development_orb: {
    label: string;
    summary_template: string;
    xp_label: string;
    level_label: string;
  };
  amplitude_bands: Record<'low' | 'medium' | 'high' | 'critical', { label: string; color: string }>;
  dimension_levels: Record<'low' | 'medium' | 'high', { label: string }>;
  xp: {
    label: string;
    /** Floor XP every task starts with. */
    base: number;
    /** Σ |contributes_to_tags| × this is the main signal. */
    weight_multiplier: number;
    cognitive_bonus: Record<'low' | 'medium' | 'high', number>;
    /**
     * Tiered duration bonus. The first bracket whose `max_minutes >= dosage.duration`
     * wins. The last bracket should use `max_minutes: null` to act as the catch-all.
     */
    duration_brackets: Array<{ max_minutes: number | null; bonus: number }>;
    /**
     * How the per-task XP is split across the 3 reportable buckets so the
     * dashboard chips stay on the same magnitude scale (no more "+135 dim
     * vs +5 tag" chasm). `dimension + tag + pendulum` should sum to 1.0.
     */
    shares: {
      dimension: number;
      tag: number;
      pendulum: number;
    };
    level_step: number;
    level_prefix: string;
    tooltip_total: string;
    tooltip_dimension: string;
    earned_prefix: string;
    no_xp_yet: string;
  };
}

export interface UnifiedContent {
  meta: { version: string };
  dimensions: Record<string, DimensionContent>;
  tags: Record<string, TagContent>;
  pendulum_axes: { axes: Record<string, PendulumAxis> };
  interventions: Record<string, Intervention>;
  ui: UICopy;
  months: MonthSpec[];
}

// Only used if taxonomy.json ever ships a dimension without a colour.
// Mirrors src/utils/dimensionMeta.ts#DIMENSION_COLORS.
const DIMENSION_COLOR_FALLBACK: Record<string, string> = {
  cognition: '#5B8FD6',
  affect: '#E66B5B',
  attention: '#4FA8C7',
  self: '#E9B44C',
  motivation: '#C77DBB',
  behavior: '#5EA68C',
  biophysiology: '#8FBF6A',
  social: '#9A7BCB',
};

async function fetchJson<T>(url: string): Promise<T> {
  // Content and scoring weights change independently of the app bundle.
  // Recalculate clears the in-memory promise cache; no-store ensures the next
  // request also bypasses a stale browser HTTP cache.
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`fetch ${url} → ${res.status}`);
  return res.json() as Promise<T>;
}

interface TaxonomyDoc {
  meta?: { version?: string };
  dimensions: Array<{ id: string; label: string; color?: string; icon?: string }>;
  tags: Array<{ id: string; label: string; polarity: 'shadow' | 'engine' }>;
  pendulum_axes: Array<{
    id: string;
    label: string;
    question?: string;
    poles: Array<{ id: string; label: string; icon?: string }>;
  }>;
}

interface WeightsDoc {
  pattern_to_dimension: Record<string, Record<string, number>>;
  pattern_to_pendulum: Record<string, Record<string, Record<string, number>>>;
  // engine tag -> axis -> damp weight (counter-evidence; see scoringEngine)
  pattern_to_pendulum_centering?: Record<string, Record<string, number>>;
}

interface DescriptionsDoc {
  dimensions: Record<string, Partial<DimensionContent>>;
  tags: Record<string, Partial<TagContent>>;
  pendulum_axes: Record<string, {
    label?: string;
    subtitle?: Subtitle;
    question?: string;
    description?: string;
    score_note?: string;
    mechanism?: string;
    real_life_examples?: string[];
    aha_insight?: string;
    flexible_middle?: string;
    how_it_helps?: string[];
    regulation_strategies?: string[];
    research?: { key_study?: string; reference?: string; source_url?: string };
    poles?: Record<string, { label?: string; description?: string }>;
  }>;
  ui?: UICopy;
  months?: MonthSpec[];
}

interface InterventionsDoc {
  interventions: Intervention[];
}

function buildUnified(args: {
  taxonomy: TaxonomyDoc;
  weights: WeightsDoc;
  descriptions: DescriptionsDoc;
  interventions: InterventionsDoc;
}): UnifiedContent {
  const { taxonomy, weights, descriptions, interventions } = args;

  const dimensions: Record<string, DimensionContent> = {};
  taxonomy.dimensions.forEach((d) => {
    const desc = descriptions.dimensions[d.id] || {};
    dimensions[d.id] = {
      id: d.id,
      label: d.label,
      color: d.color || DIMENSION_COLOR_FALLBACK[d.id],
      icon: d.icon,
      ...desc,
    };
  });

  const tags: Record<string, TagContent> = {};
  taxonomy.tags.forEach((t) => {
    const desc = descriptions.tags[t.id] || {};
    tags[t.id] = {
      id: t.id,
      label: t.label,
      type: t.polarity,
      ...desc,
      dimension_weights: weights.pattern_to_dimension[t.id] || {},
      pendulum_weights: weights.pattern_to_pendulum[t.id] || {},
    };
  });

  const axes: Record<string, PendulumAxis> = {};
  taxonomy.pendulum_axes.forEach((a) => {
    const desc = descriptions.pendulum_axes[a.id] || {};
    const polesMap: Record<string, PoleConfig> = {};
    a.poles.forEach((p) => {
      polesMap[p.id] = {
        label: desc.poles?.[p.id]?.label || p.label,
        icon: p.icon,
        description: desc.poles?.[p.id]?.description,
      };
    });

    // Invert pattern_to_pendulum → axis.weights[poleId][tagId] = w (consumed by scoringEngine).
    const axisWeights: Record<string, Record<string, number>> = {};
    a.poles.forEach((p) => { axisWeights[p.id] = {}; });
    Object.entries(weights.pattern_to_pendulum).forEach(([tagId, axesMap]) => {
      const polesForAxis = axesMap[a.id];
      if (!polesForAxis) return;
      Object.entries(polesForAxis).forEach(([poleId, w]) => {
        axisWeights[poleId] = axisWeights[poleId] || {};
        axisWeights[poleId][tagId] = w;
      });
    });

    // Invert pattern_to_pendulum_centering → axis.centering[tagId] = w.
    const centering: Record<string, number> = {};
    Object.entries(weights.pattern_to_pendulum_centering || {}).forEach(([tagId, axesMap]) => {
      if (tagId.startsWith('_')) return; // _comment
      const w = axesMap[a.id];
      if (typeof w === 'number' && w > 0) centering[tagId] = w;
    });

    axes[a.id] = {
      id: a.id,
      label: desc.label || a.label,
      subtitle: desc.subtitle,
      question: desc.question || a.question,
      description: desc.description,
      score_note: desc.score_note,
      mechanism: desc.mechanism,
      real_life_examples: desc.real_life_examples,
      aha_insight: desc.aha_insight,
      flexible_middle: desc.flexible_middle,
      how_it_helps: desc.how_it_helps,
      regulation_strategies: desc.regulation_strategies,
      research: desc.research,
      left_pole: a.poles[0]?.id,
      right_pole: a.poles[1]?.id,
      poles: polesMap,
      weights: axisWeights,
      centering,
    };
  });

  const interventionsMap: Record<string, Intervention> = {};
  interventions.interventions.forEach((iv) => { interventionsMap[iv.id] = iv; });

  return {
    meta: { version: taxonomy.meta?.version || '2.0.0' },
    dimensions,
    tags,
    pendulum_axes: { axes },
    interventions: interventionsMap,
    ui: descriptions.ui ?? UI_FALLBACK,
    months: (descriptions.months && descriptions.months.length > 0)
      ? descriptions.months
      : MONTHS_FALLBACK,
  };
}

// Fallbacks keep the app rendering if the content is loaded from an older
// build that predates the ui/months blocks. Production content always ships
// these - see content/descriptions.en.json.
const UI_FALLBACK: UICopy = {
  greetings: { night: 'Good night', morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening', fallback_name: 'there' },
  nav: {
    dashboard: 'Dashboard',
    sign_out: 'Sign Out',
    recalculate: '↻ Recalculate',
    recalculate_tooltip: 'Rebuild your roadmap from your latest assessment',
    recalculate_confirm: 'Recalculate roadmap?',
  },
  sections: {
    dimensions: { title: 'Your dimensions', subtitle: '', meta_label: 'MEAN' },
    patterns: { title: 'All patterns', subtitle: '', meta_label: 'ACTIVE', empty: 'No patterns detected.', xp_tooltip: 'XP earned' },
    pendulums: { title: 'Pendulums', subtitle: '', meta_label: 'TRACKED', xp_tooltip: 'XP earned' },
    roadmap: {
      title: 'Your roadmap',
      subtitle: '',
      stat_done_label: 'Tasks done',
      stat_complete_label: 'Complete',
      month_eyebrow: 'Month {index}',
      panel_eyebrow: 'MONTH {index}',
      locked_label: 'Locked',
      locked_hint: 'Finish Month {prev} to unlock.',
      task_research_prefix: '',
      task_contributes_label: 'Adds XP to',
      task_how_label: 'How to do it',
      task_why_label: 'Why this works',
      task_more_label: 'More',
      task_less_label: 'Less',
      metrics_title: 'METRICS',
      retake_title: 'Retake whenever you want',
      retake_text: 'Recommended monthly. Your scores recompute and the next 12 months rebuild.',
      retake_button: 'Retake →',
    },
  },
  today_focus: {
    eyebrow: "TODAY'S FOCUS",
    cta_pending: 'Start now',
    cta_done: 'Completed today',
    research_prefix: '',
    contributes_label: 'Adds XP to',
    how_label: 'How to do it',
    why_label: 'Why this works',
    more_label: 'More',
    less_label: 'Less',
    duration_prefix: '⏱',
  },
  states: {
    loading: 'Loading…',
    no_results_icon: '📋',
    no_results_title: 'No results',
    no_results_body: 'Take the quiz to see your profile.',
    no_results_cta: 'Take the assessment →',
    paywall_icon: '🎯',
    paywall_title: 'Unlock your dashboard',
    paywall_body: 'Purchase to unlock your full diagnostic.',
    paywall_cta: 'Choose your plan →',
  },
  development_orb: {
    label: 'DEVELOPMENT SCORE',
    summary_template: 'Strongest {strongLabel} ({strongPct}%). Weakest {weakLabel} ({weakPct}%).',
    xp_label: 'Total pts',
    level_label: 'Stage',
  },
  amplitude_bands: {
    low: { label: 'Stable', color: '#22C55E' },
    medium: { label: 'Active', color: '#F59E0B' },
    high: { label: 'Wide swing', color: '#EF4444' },
    critical: { label: 'Critical swing', color: '#DC2626' },
  },
  dimension_levels: {
    low: { label: 'Needs work' },
    medium: { label: 'Functional' },
    high: { label: 'Strong' },
  },
  xp: {
    label: 'pts',
    base: 40,
    weight_multiplier: 220,
    cognitive_bonus: { low: 0, medium: 25, high: 60 },
    duration_brackets: [
      { max_minutes: 5, bonus: 0 },
      { max_minutes: 15, bonus: 15 },
      { max_minutes: 30, bonus: 30 },
      { max_minutes: null, bonus: 50 },
    ],
    shares: { dimension: 0.5, tag: 0.3, pendulum: 0.2 },
    level_step: 1200,
    level_prefix: 'Stage',
    tooltip_total: 'Total points earned across completed tasks.',
    tooltip_dimension: 'Points earned in this dimension.',
    earned_prefix: '+',
    no_xp_yet: 'Complete tasks to earn points',
  },
};

const MONTHS_FALLBACK: MonthSpec[] = Array.from({ length: 12 }, (_, i) => ({
  id: `month-${i + 1}`,
  label: `Month ${i + 1}`,
  tagline: '',
  narrative: '',
}));

const V2_BASE = '/data/content';

const cache: Map<string, Promise<UnifiedContent>> = new Map();

/**
 * Loads unified content for a language. taxonomy + weights are shared (IDs and
 * numbers, language-agnostic). descriptions + interventions are per-language
 * with an EN fallback when a translated file is missing (404).
 */
export function loadContent(lang: string = 'en'): Promise<UnifiedContent> {
  const cached = cache.get(lang);
  if (cached) return cached;

  const promise = (async () => {
    const [taxonomy, weights, descriptions, interventions] = await Promise.all([
      fetchJson<TaxonomyDoc>(`${V2_BASE}/taxonomy.json`),
      fetchJson<WeightsDoc>(`${V2_BASE}/weights.json`),
      fetchJson<DescriptionsDoc>(`${V2_BASE}/descriptions.${lang}.json`).catch(() =>
        fetchJson<DescriptionsDoc>(`${V2_BASE}/descriptions.en.json`),
      ),
      fetchJson<InterventionsDoc>(`${V2_BASE}/interventions.${lang}.json`).catch(() =>
        fetchJson<InterventionsDoc>(`${V2_BASE}/interventions.en.json`),
      ),
    ]);
    return buildUnified({ taxonomy, weights, descriptions, interventions });
  })();

  cache.set(lang, promise);
  return promise;
}

export function clearContentCache(): void {
  cache.clear();
}
